import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useScrollTrigger } from '@uhg-abyss/web/hooks/useScrollTrigger';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout as AbyssLayout } from '@uhg-abyss/web/ui/Layout';
import { motion } from 'framer-motion';
import { isNil, isNull } from 'lodash';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { Styles } from './includes/styles';
import { Header } from './sections/Header';
import { Main } from './sections/Main';
import { Sidebar } from './sections/Sidebar';

/**
 * Layout: Dashboard
 *
 * Glues together the various pieces of the layout.
 *
 * @param children
 * @param hasData
 * @param header
 * @param isFetching
 * @param isLoading
 * @param title
 * @param widgets
 * @returns {Element}
 * @constructor
 */
export function Dashboard({
  children,
  hasData = false,
  header = {},
  isFetching = false,
  isLoading = false,
  title = '',
  widgets = [],
}) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(null);
  const [currentTarget, setCurrentTarget] = useState(null);

  const { scrollIntoView, targetRef } = useScrollTrigger({ offset: 180 });

  const router = useRouter();
  const location = router.getLocation();
  const currentAccessor = location?.hash?.replace('#', '');

  /**
   * handles the click event for the expand/collapse button
   * @param value
   */
  const toggleSidebar = (value = null) => {
    if (!isNull(value)) {
      setIsSidebarOpen(value);
    } else {
      setIsSidebarOpen((prev) => {
        return !prev;
      });
    }
  };

  /**
   * reset when searching
   */
  useEffect(() => {
    setIsSidebarOpen(null);
  }, [isFetching]);

  /**
   * sets the current target to the current accessor
   */
  useLayoutEffect(() => {
    if (!isLoading && !isFetching && hasData && !isNil(currentAccessor) && currentAccessor != currentTarget) {
      setCurrentTarget(currentAccessor);
    }
  }, [isLoading, isFetching, hasData, currentAccessor]);

  /**
   * scrolls to the current target when the current target changes
   */
  useLayoutEffect(() => {
    scrollIntoView();
  }, [currentTarget, targetRef]);

  return (
    <ErrorHandler location="src/shared/ui/layouts/Dashboard/Dashboard.jsx">
      <Styles>
        <motion.div
          animate="open"
          initial={{ opacity: 0 }}
          variants={{
            closed: { opacity: 0 },
            open: { opacity: 1 },
          }}
        >
          <Grid id="layout-dashboard">
            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              <Header
                components={header}
                hasData={hasData}
                isFetching={isFetching}
                isLoading={isLoading}
                title={title}
              />
            </Grid.Col>
            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              {!isLoading && (
                <AbyssLayout.Group
                  alignItems="top"
                  css={{
                    '> div': {
                      height: '100%',
                    },
                    '> div:last-child': {
                      width: 'calc(100% - 312px)',
                    },
                    height: '100%',
                  }}
                  space={themeConfiguration?.theme?.space?.md}
                >
                  <Sidebar
                    currentAccessor={currentAccessor}
                    hasData={hasData}
                    isFetching={isFetching}
                    isOpen={isSidebarOpen}
                    setCurrentTarget={setCurrentTarget}
                    toggleSidebar={toggleSidebar}
                    widgets={widgets}
                  />
                  <Main
                    currentTarget={currentTarget}
                    hasData={hasData}
                    isFetching={isFetching}
                    targetRef={targetRef}
                    widgets={widgets}
                  >
                    {children}
                  </Main>
                </AbyssLayout.Group>
              )}
            </Grid.Col>
          </Grid>
        </motion.div>
      </Styles>
    </ErrorHandler>
  );
}
